import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { fetchJobProgress, cancelBacktestJob, JOB_STATUS } from '../../services/backtestService';

// SVG Icons
const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const CheckCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const ErrorCircleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

/**
 * BacktestProgressModal Component
 * 
 * Shows progress of an async backtest job with cancel support.
 * Polls for updates and navigates to results on completion.
 * 
 * @param {Object} props
 * @param {Object} props.job - Initial job object with id, strategyName
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onComplete - Optional callback when job completes
 */
const BacktestProgressModal = ({ job, onClose, onComplete }) => {
    const navigate = useNavigate();
    const [status, setStatus] = useState(job?.status || JOB_STATUS.PENDING);
    const [progress, setProgress] = useState(job?.progress || 0);
    const [currentChunk, setCurrentChunk] = useState(0);
    const [totalChunks, setTotalChunks] = useState(0);
    const [resultId, setResultId] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [cancelling, setCancelling] = useState(false);

    const pollIntervalRef = useRef(null);
    const isTerminalState = [JOB_STATUS.COMPLETED, JOB_STATUS.FAILED, JOB_STATUS.CANCELLED].includes(status);

    // Poll for job progress
    const pollProgress = useCallback(async () => {
        if (!job?.id) return;

        try {
            const progressData = await fetchJobProgress(job.id);
            setStatus(progressData.status);
            setProgress(progressData.progress || 0);
            setCurrentChunk(progressData.currentChunk || 0);
            setTotalChunks(progressData.totalChunks || 0);

            if (progressData.status === JOB_STATUS.COMPLETED) {
                setResultId(progressData.resultId || progressData.backtestResultId);
            } else if (progressData.status === JOB_STATUS.FAILED) {
                setErrorMessage(progressData.errorMessage || 'Backtest failed');
            }
        } catch (error) {
            console.error('Error fetching job progress:', error);
            setErrorMessage(error.message || 'Failed to fetch progress');
        }
    }, [job?.id]);

    // Start polling on mount
    useEffect(() => {
        if (!job?.id || isTerminalState) return;

        // Initial fetch
        pollProgress();

        // Poll every 2 seconds
        pollIntervalRef.current = setInterval(pollProgress, 2000);

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, [job?.id, isTerminalState, pollProgress]);

    // Handle cancel
    const handleCancel = async () => {
        if (!job?.id || cancelling) return;

        setCancelling(true);
        try {
            await cancelBacktestJob(job.id);
            setStatus(JOB_STATUS.CANCELLED);
        } catch (error) {
            console.error('Error cancelling job:', error);
            setErrorMessage(error.message || 'Failed to cancel');
        } finally {
            setCancelling(false);
        }
    };

    // Navigate to results
    const handleViewResults = () => {
        if (resultId) {
            onComplete?.(resultId);
            navigate(`/dashboard/backtest/results/${resultId}`);
        }
        onClose?.();
    };

    // Render status badge
    const renderStatusBadge = () => {
        const badges = {
            [JOB_STATUS.PENDING]: 'badge-warning',
            [JOB_STATUS.RUNNING]: 'badge-info',
            [JOB_STATUS.COMPLETED]: 'badge-success',
            [JOB_STATUS.FAILED]: 'badge-error',
            [JOB_STATUS.CANCELLED]: 'badge-neutral',
        };

        return (
            <span className={`badge ${badges[status] || 'badge-ghost'}`}>
                {status}
            </span>
        );
    };

    // Render content based on status
    const renderContent = () => {
        if (status === JOB_STATUS.COMPLETED) {
            return (
                <div className="text-center py-6">
                    <CheckCircleIcon />
                    <h3 className="text-xl font-bold mt-4 text-success">Backtest Complete!</h3>
                    <p className="text-base-content/60 mt-2">
                        Your backtest for "{job?.strategyName}" has finished successfully.
                    </p>
                    <button className="btn btn-success mt-6" onClick={handleViewResults}>
                        View Results
                    </button>
                </div>
            );
        }

        if (status === JOB_STATUS.FAILED) {
            return (
                <div className="text-center py-6">
                    <ErrorCircleIcon />
                    <h3 className="text-xl font-bold mt-4 text-error">Backtest Failed</h3>
                    <p className="text-base-content/60 mt-2">
                        {errorMessage || 'An error occurred during backtest execution.'}
                    </p>
                    <button className="btn btn-ghost mt-6" onClick={onClose}>
                        Close
                    </button>
                </div>
            );
        }

        if (status === JOB_STATUS.CANCELLED) {
            return (
                <div className="text-center py-6">
                    <div className="text-5xl mb-4">🛑</div>
                    <h3 className="text-xl font-bold">Backtest Cancelled</h3>
                    <p className="text-base-content/60 mt-2">
                        The backtest was cancelled.
                    </p>
                    <button className="btn btn-ghost mt-6" onClick={onClose}>
                        Close
                    </button>
                </div>
            );
        }

        // PENDING or RUNNING
        return (
            <div className="py-4">
                <div className="flex items-center justify-between mb-4">
                    <span className="text-base-content/60">Status:</span>
                    {renderStatusBadge()}
                </div>

                {/* Progress bar */}
                <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                        <span>Progress</span>
                        <span>{progress}%</span>
                    </div>
                    <progress
                        className="progress progress-info w-full"
                        value={progress}
                        max="100"
                    />
                </div>

                {/* Chunk info */}
                {totalChunks > 0 && (
                    <div className="text-sm text-base-content/60 mb-4">
                        Processing day {currentChunk} of {totalChunks}
                    </div>
                )}

                {/* Status message */}
                <div className="text-center text-base-content/60">
                    {status === JOB_STATUS.PENDING && (
                        <div className="flex items-center justify-center gap-2">
                            <span className="loading loading-dots loading-sm"></span>
                            <span>Waiting to start...</span>
                        </div>
                    )}
                    {status === JOB_STATUS.RUNNING && (
                        <div className="flex items-center justify-center gap-2">
                            <span className="loading loading-spinner loading-sm"></span>
                            <span>Running backtest...</span>
                        </div>
                    )}
                </div>

                {/* Cancel button */}
                <div className="mt-6 flex justify-center">
                    <button
                        className="btn btn-outline btn-error btn-sm gap-2"
                        onClick={handleCancel}
                        disabled={cancelling}
                    >
                        {cancelling && <span className="loading loading-spinner loading-xs"></span>}
                        Cancel Backtest
                    </button>
                </div>
            </div>
        );
    };

    if (!job) return null;

    return createPortal(
        <dialog className="modal modal-open">
            <div className="modal-box max-w-md">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-bold text-lg">Backtest Progress</h2>
                    {isTerminalState && (
                        <button className="btn btn-sm btn-circle btn-ghost" onClick={onClose}>
                            <CloseIcon />
                        </button>
                    )}
                </div>

                {/* Strategy info */}
                <div className="text-sm text-base-content/60 mb-4">
                    Strategy: <span className="font-medium text-base-content">{job.strategyName}</span>
                </div>

                {/* Content */}
                {renderContent()}
            </div>

            {/* Backdrop - only closeable in terminal states */}
            {isTerminalState && (
                <form method="dialog" className="modal-backdrop">
                    <button onClick={onClose}>close</button>
                </form>
            )}
        </dialog>,
        document.body
    );
};

export default BacktestProgressModal;
